import { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import { useDialers } from '@/features/dialers/api';
import { asFormString } from '@/features/users/format';
import { useTrunkDetail, useUpdateTrunk } from '../api';
import { trunkUpdateSchema, type TrunkUpdateInput } from '../schema';

export function UpdateTrunkPage() {
  const navigate = useNavigate();
  const { trunkId: trunkIdParam } = useParams<{ trunkId: string }>();
  const trunkId = Number(trunkIdParam);

  const detail = useTrunkDetail(trunkId);
  const dialers = useDialers();
  const update = useUpdateTrunk();

  const dialerOptions = useMemo(
    () =>
      (dialers.data ?? []).map((d) => ({
        value: String(d.dialerId),
        label: d.dialerName,
      })),
    [dialers.data],
  );

  const form = useForm<TrunkUpdateInput>({
    resolver: zodResolver(trunkUpdateSchema),
    mode: 'onTouched',
    defaultValues: {
      trunkName: '',
      pilotNumber: '',
      channels: '',
      cps: '',
      dialerId: '',
    },
  });

  useEffect(() => {
    if (!detail.data) return;
    const d = detail.data;
    /* The detail endpoint returns `dialerName`, not `dialerId`. Resolve
     * by matching the name against our useDialers cache once both load. */
    const matched = (dialers.data ?? []).find((opt) => opt.dialerName === d.dialerName);
    form.reset({
      trunkName: asFormString(d.trunkName),
      pilotNumber: asFormString(d.pilotNumber),
      channels: asFormString(d.channels),
      cps: asFormString(d.cps),
      dialerId: matched ? String(matched.dialerId) : '',
    });
  }, [detail.data, dialers.data, form]);

  const onSubmit = (input: TrunkUpdateInput) => {
    update.mutate(
      {
        trunkId,
        trunkName: input.trunkName.trim(),
        pilotNumber: input.pilotNumber.trim(),
        channels: input.channels,
        cps: input.cps,
        dialerId: Number(input.dialerId),
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? 'Trunk updated');
          navigate('/trunk-list');
        },
        onError: (err) => toast.error(readErrorMessage(err) ?? 'Could not update the trunk.'),
      },
    );
  };

  return (
    <ScrollPage>
      <PageHeader
        title="Update Trunk"
        description={
          detail.data ? `Editing "${detail.data.trunkName}".` : 'Loading trunk details…'
        }
        actions={
          <Button asChild variant="outline">
            <Link to="/trunk-list">
              <ArrowLeft className="h-4 w-4" />
              Back to trunks
            </Link>
          </Button>
        }
      />
      <Form {...form}>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
          <Card>
            <CardHeader>
              <CardTitle>Trunk details</CardTitle>
              <CardDescription>Edit carrier identifier, capacity, and dialer link.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="trunkName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Trunk name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="pilotNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pilot number</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="channels"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Channels</FormLabel>
                    <FormControl>
                      <Input {...field} type="number" inputMode="numeric" min="0" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="cps"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CPS (calls per second)</FormLabel>
                    <FormControl>
                      <Input {...field} type="number" inputMode="numeric" min="0" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="dialerId"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>Dialer</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={dialers.isLoading}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={dialers.isLoading ? 'Loading…' : 'Pick a dialer'} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {dialerOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex items-center justify-end gap-2">
            <Button asChild variant="ghost">
              <Link to="/trunk-list">Cancel</Link>
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save changes
            </Button>
          </div>
        </form>
      </Form>
    </ScrollPage>
  );
}

function readErrorMessage(err: unknown): string | undefined {
  if (err && typeof err === 'object') {
    const a = err as { response?: { data?: { message?: string } }; message?: string };
    return a.response?.data?.message ?? a.message;
  }
  return undefined;
}
